import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Plus, Search, X, Loader2, Trash2, Copy, Pencil,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Maximize2, Minimize2,
  Image as ImageIcon, MoreHorizontal, AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adsApi, AdListItem, AdsMeta } from '../../lib/api/ads'
import { AdRowEditor } from './AdRowEditor'

interface AdsFeedTableProps {
  campaignId: number
  adUnitId: number
  activeAdId: number | null
  onSelectAd: (adId: number | null) => void
  onAdsCountChange?: (count: number) => void
  /**
   * Fires when an ad row was created/updated/duplicated/deleted via the
   * inline editor. The parent uses this to bust caches on the preview
   * iframe (the static preview backend returns stale HTML otherwise).
   */
  onAdsMutated?: () => void
  /**
   * Forwarded to AdRowEditor → Trackers tab. The parent owns the
   * ad-unit fetch so it's the natural place to source these.
   */
  adUnitSizes?: string[]
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  ready_to_publish: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
  draft: { bg: 'bg-gray-50 dark:bg-gray-800/60', text: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400' },
  published: { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  archived: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
  paused: { bg: 'bg-orange-50 dark:bg-orange-900/20', text: 'text-orange-700 dark:text-orange-400', dot: 'bg-orange-500' },
}

const DEFAULT_STATUS = { bg: 'bg-gray-50 dark:bg-gray-800/60', text: 'text-gray-600 dark:text-gray-400', dot: 'bg-gray-400' }

function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
  } catch { return dateStr }
}

export function AdsFeedTable({
  campaignId,
  adUnitId,
  activeAdId,
  onSelectAd,
  onAdsCountChange,
  onAdsMutated,
  adUnitSizes,
}: AdsFeedTableProps) {
  const [ads, setAds] = useState<AdListItem[]>([])
  const [meta, setMeta] = useState<AdsMeta | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [editingAdId, setEditingAdId] = useState<number | 'new' | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [contextMenuAdId, setContextMenuAdId] = useState<number | null>(null)
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null)
  const [isCopyingId, setIsCopyingId] = useState<number | null>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchAds = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const result = await adsApi.getAds(campaignId, adUnitId, {
        page: currentPage,
        per_page: 25,
        q: debouncedQuery || undefined,
      })
      setAds(result.ads)
      setMeta(result.meta)
      onAdsCountChange?.(result.meta.total_count)
      // ── Auto-select the first variant ──
      // When the user opens the creative studio we want them to land on
      // a usable preview immediately. If nothing is selected, or the
      // currently active ad isn't part of this page of results (e.g.
      // after a search), fall back to the first ad in the list. Empty
      // pages just clear the selection.
      const ids = result.ads.map(a => a.id)
      if (ids.length === 0) {
        if (activeAdId !== null) onSelectAd(null)
      } else if (activeAdId === null || !ids.includes(activeAdId)) {
        onSelectAd(ids[0])
      }
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load variants')
    } finally {
      setIsLoading(false)
    }
    // We intentionally omit `activeAdId` / `onSelectAd` from the deps —
    // including them would re-run the fetch on every selection change.
    // The fetch only needs to run for campaign/ad-unit/page/query
    // changes; the auto-select inside is safe to read the latest values
    // off the closure since it only ever moves the selection *into* a
    // valid state when a new result lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, adUnitId, currentPage, debouncedQuery, onAdsCountChange])

  useEffect(() => { fetchAds() }, [fetchAds])

  useEffect(() => {
    if (!contextMenuAdId) return
    const handler = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenuAdId(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [contextMenuAdId])

  const toggleRow = (id: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleDelete = async (adId: number) => {
    setIsDeletingId(adId)
    try {
      await adsApi.deleteAd(campaignId, adUnitId, adId)
      toast.success('Variant deleted')
      if (editingAdId === adId) setEditingAdId(null)
      if (activeAdId === adId) onSelectAd(null)
      fetchAds()
      onAdsMutated?.()
    } catch { toast.error('Failed to delete variant') }
    finally { setIsDeletingId(null); setContextMenuAdId(null) }
  }

  const handleCopy = async (adId: number) => {
    setIsCopyingId(adId)
    try {
      await adsApi.copyAd(campaignId, adUnitId, adId)
      toast.success('Variant duplicated')
      fetchAds()
      onAdsMutated?.()
    } catch { toast.error('Failed to duplicate variant') }
    finally { setIsCopyingId(null); setContextMenuAdId(null) }
  }

  const handleEditorSave = () => { setEditingAdId(null); fetchAds(); onAdsMutated?.() }
  const handleEditorCancel = () => { setEditingAdId(null) }

  const totalPages = meta?.total_pages || 1

  const paginationRange = useMemo(() => {
    const pages: (number | '...')[] = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (currentPage > 3) pages.push('...')
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (currentPage < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }, [totalPages, currentPage])

  // ─── Row renderer ───

  function renderRow(ad: AdListItem) {
    const isActive = ad.id === activeAdId
    const isSelected = selectedRows.has(ad.id)
    const isEditing = editingAdId === ad.id
    const sc = STATUS_COLORS[ad.status] || DEFAULT_STATUS

    return (
      <div key={ad.id} className={isActive ? 'relative z-[1]' : undefined}>
        <div
          onClick={() => { if (!isEditing) onSelectAd(ad.id) }}
          /* Active row gets a stronger pale-primary fill, a 3px left accent
             bar (inset box-shadow) and a 1px outer outline so the selection
             is unmistakable in long lists in both light and dark modes. */
          /* Active row keeps a strong left accent + outline so it's
             unmistakable, but the fill stays subtle so it doesn't dominate
             the panel visually. */
          className={`group flex items-center gap-2.5 px-3 py-2.5 border-b cursor-pointer transition-colors ${
            isActive
              ? 'bg-primary-50/70 dark:bg-primary-900/20 border-primary-200/80 dark:border-primary-800/60 shadow-[inset_3px_0_0_0_#6fa63f] dark:shadow-[inset_3px_0_0_0_#a2d160] outline outline-1 -outline-offset-1 outline-primary-300/80 dark:outline-primary-700/70'
              : isSelected
              ? 'bg-blue-50/60 dark:bg-blue-900/15 border-gray-100 dark:border-gray-800/50'
              : 'border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-900/40'
          }`}
        >
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={isSelected}
            onChange={e => { e.stopPropagation(); toggleRow(ad.id) }}
            onClick={e => e.stopPropagation()}
            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent flex-shrink-0"
          />

          {/* Thumbnail */}
          <div className="flex-shrink-0">
            {ad.image_url ? (
              <img
                src={ad.image_url}
                alt={ad.title || 'Variant'}
                className="w-10 h-10 rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                <ImageIcon className="h-4 w-4 text-gray-300 dark:text-gray-600" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: primary label.
                For variant-backed ads the variant attribute's `name`
                (e.g. "AI Generated Variant 1") is what users actually
                identify a row by, so we promote it to the primary line.
                When the variant attribute exists but its `name` is null
                (older variants, or freshly-generated ones the user
                hasn't named yet), fall back to a deterministic
                `Variant #<variant_attribute.id>` so the row never just
                reads "Untitled" — users still need a way to tell rows
                apart in the list. The classic `ad.title` is shown as a
                secondary chip when both are present so we don't lose
                either piece of info. */}
            <div className="flex items-center gap-1.5 min-w-0">
              {(() => {
                const variantName = ad.variant_attribute?.name?.trim()
                const variantFallback = ad.variant_attribute
                  ? `Variant #${ad.variant_attribute.id}`
                  : null
                const primary = variantName || ad.title || variantFallback
                return (
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                    {primary || <span className="italic text-gray-400">Untitled</span>}
                  </p>
                )
              })()}
              {ad.variant_attribute?.name && ad.title && (
                <span
                  className="text-[10px] px-1 py-px rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex-shrink-0 truncate max-w-[140px]"
                  title={ad.title}
                >
                  {ad.title}
                </span>
              )}
            </div>
            {/* Row 2: destination URL */}
            {ad.destination_url && (
              <p className="text-[10px] text-primary-600/70 dark:text-primary-400/70 truncate mt-0.5" title={ad.destination_url}>
                {ad.destination_url}
              </p>
            )}
            {/* Row 3: ID | status | CTA | date */}
            <div className="flex items-center gap-1.5 mt-1 text-[10px]">
              <span className="text-gray-500 dark:text-gray-400 font-mono flex-shrink-0">#{ad.id}</span>
              <span className="text-gray-300 dark:text-gray-600 flex-shrink-0">&middot;</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${sc.bg} ${sc.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                {formatStatus(ad.status)}
              </span>
              {ad.action_text && (
                <>
                  <span className="text-gray-300 dark:text-gray-600 flex-shrink-0">&middot;</span>
                  <span className="text-gray-500 dark:text-gray-400 truncate">{ad.action_text}</span>
                </>
              )}
              <span className="text-gray-300 dark:text-gray-600 flex-shrink-0">&middot;</span>
              <span className="text-gray-400 dark:text-gray-500 flex-shrink-0">{formatDate(ad.updated_at)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-0.5 flex-shrink-0 relative">
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={e => { e.stopPropagation(); onSelectAd(ad.id); setEditingAdId(ad.id) }}
                className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Edit"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={e => { e.stopPropagation(); setContextMenuAdId(contextMenuAdId === ad.id ? null : ad.id) }}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="More"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </div>

            {contextMenuAdId === ad.id && (
              <div
                ref={contextMenuRef}
                className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 py-1"
              >
                <button
                  onClick={e => { e.stopPropagation(); onSelectAd(ad.id); setEditingAdId(ad.id); setContextMenuAdId(null) }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={e => { e.stopPropagation(); handleCopy(ad.id) }}
                  disabled={isCopyingId === ad.id}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-40"
                >
                  {isCopyingId === ad.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                  Duplicate
                </button>
                <div className="h-px bg-gray-100 dark:bg-gray-800 my-1" />
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(ad.id) }}
                  disabled={isDeletingId === ad.id}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                >
                  {isDeletingId === ad.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="border-b-2 border-primary-200 dark:border-primary-800 bg-gray-50 dark:bg-gray-900/60">
            <AdRowEditor
              campaignId={campaignId}
              adUnitId={adUnitId}
              adId={ad.id}
              onSave={handleEditorSave}
              onCancel={handleEditorCancel}
              adUnitSizes={adUnitSizes}
            />
          </div>
        )}
      </div>
    )
  }

  // ─── Table content ───

  const tableContent = (expanded = false) => (
    <div className={`${expanded ? 'flex-1 overflow-auto' : 'flex-1 overflow-auto min-h-0'}`}>
      {isLoading && ads.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full p-6">
          <Loader2 className="h-6 w-6 text-primary-600 animate-spin mb-2" />
          <p className="text-xs text-gray-500 dark:text-gray-400">Loading variants...</p>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <AlertCircle className="h-6 w-6 text-red-500 mb-2" />
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Failed to load</p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{loadError}</p>
          <button onClick={fetchAds} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Retry</button>
        </div>
      ) : ads.length === 0 && !editingAdId ? (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
            <Plus className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">No variants yet</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 max-w-[200px]">
            Create your first variant for this ad unit
          </p>
          <button
            onClick={() => setEditingAdId('new')}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Create first variant
          </button>
        </div>
      ) : (
        <div>
          {editingAdId === 'new' && (
            <div className="border-b-2 border-primary-200 dark:border-primary-800 bg-gray-50 dark:bg-gray-900/60">
              <AdRowEditor
                campaignId={campaignId}
                adUnitId={adUnitId}
                adId={null}
                onSave={handleEditorSave}
                onCancel={handleEditorCancel}
                adUnitSizes={adUnitSizes}
              />
            </div>
          )}
          {ads.map(ad => renderRow(ad))}
        </div>
      )}
    </div>
  )

  // ─── Main render ───

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex-shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
            Variants{meta ? ` (${meta.total_count})` : ''}
          </h3>
          {isLoading && ads.length > 0 && (
            <Loader2 className="h-3 w-3 text-primary-500 animate-spin flex-shrink-0" />
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {showSearch ? (
            <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
              <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search..."
                autoFocus
                className="bg-transparent text-xs text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none w-24"
              />
              <button onClick={() => { setShowSearch(false); setSearchQuery(''); setDebouncedQuery('') }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <button onClick={() => setShowSearch(true)} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Search">
              <Search className="h-3.5 w-3.5" />
            </button>
          )}

          <button
            onClick={() => setEditingAdId('new')}
            disabled={editingAdId === 'new'}
            className="p-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
            title="Add variant"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>

          <button onClick={() => setIsExpanded(true)} className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Expand fullscreen">
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {tableContent()}

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">{(currentPage - 1) * 25 + 1}</span>
            {' '}&ndash;{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{Math.min(currentPage * 25, meta.total_count)}</span>
            {' '}of{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{meta.total_count}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1 || isLoading} className="p-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="First page">
              <ChevronsLeft className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading} className="p-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Previous page">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-center gap-0.5 mx-0.5">
              {paginationRange.map((page, i) =>
                page === '...' ? (
                  <span key={`dots-${i}`} className="px-1 text-[11px] text-gray-400">...</span>
                ) : (
                  <button key={page} onClick={() => setCurrentPage(page as number)} disabled={isLoading} className={`min-w-[24px] h-6 rounded text-[11px] font-medium transition-colors ${currentPage === page ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                    {page}
                  </button>
                )
              )}
            </div>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || isLoading} className="p-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Next page">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || isLoading} className="p-1 rounded text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Last page">
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Fullscreen modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white dark:bg-gray-950">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Variants {meta ? `(${meta.total_count})` : ''}
              </h2>
              {showSearch ? (
                <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5">
                  <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search variants..." autoFocus className="bg-transparent text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none w-48" />
                  <button onClick={() => { setShowSearch(false); setSearchQuery(''); setDebouncedQuery('') }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button onClick={() => setShowSearch(true)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Search">
                  <Search className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditingAdId('new')} disabled={editingAdId === 'new'} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" /> Add variant
              </button>
              <button onClick={() => setIsExpanded(false)} className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Exit fullscreen">
                <Minimize2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          {tableContent(true)}
          {meta && meta.total_pages > 1 && (
            <div className="flex items-center justify-between px-5 py-2.5 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{(currentPage - 1) * 25 + 1}</span>
                {' '}to{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{Math.min(currentPage * 25, meta.total_count)}</span>
                {' '}of{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300">{meta.total_count}</span> results
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1 || isLoading} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="First page">
                  <ChevronsLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || isLoading} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Previous page">
                  <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <div className="flex items-center gap-1 mx-1">
                  {paginationRange.map((page, i) =>
                    page === '...' ? (
                      <span key={`dots-${i}`} className="px-2 text-gray-400 dark:text-gray-500">...</span>
                    ) : (
                      <button key={page} onClick={() => setCurrentPage(page as number)} disabled={isLoading} className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || isLoading} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Next page">
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || isLoading} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Last page">
                  <ChevronsRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
